// Copyright (c) Docugami, Inc. All rights reserved.

import { skipToken } from "@reduxjs/toolkit/dist/query";
import { disabledGreyText, themeBlue, themeGreen, themeOrange } from "components/shell/presentation/AppStyles";
import { useAppSelector } from "hooks/redux";
import { useGetDocSetStatusQuery } from "store/frontend/frontendSlice";
import { selectDocSetMinFiles } from "store/organization/organizationSlice";
import { createGetDocSetDocuments } from "store/state_helpers";

export enum DocSetStatus {
    None = 'None',
    Ready = 'Ready',
    Incomplete = 'Incomplete',
    Copying = 'Copying'
}

export interface DocSetStatusInfo {
    status: DocSetStatus;
    label: string;
    details: string;
    statusColor: string;
}

function getDocSetStatusLabel(status: DocSetStatus): string {
    switch (status) {
        case DocSetStatus.Copying:
            return "Copying";
        case DocSetStatus.Incomplete:
            return "Incomplete";
        case DocSetStatus.Ready:
            return "Ready";
        case DocSetStatus.None:
        default:
            return "Not Ready";
    }
}

function getDocSetStatusColor(status: DocSetStatus): string {
    switch (status) {
        case DocSetStatus.Copying:
            return themeBlue;
        case DocSetStatus.Incomplete:
            return themeOrange;
        case DocSetStatus.Ready:
            return themeGreen;
        case DocSetStatus.None:
        default:
            return disabledGreyText;
    }
}

export const getDocSetDocuments = createGetDocSetDocuments();
export function useDocSetStatus(docSetId: string): { isLoading: boolean, info: DocSetStatusInfo | undefined } {
    const docSetDocThreshold = useAppSelector(selectDocSetMinFiles);
    const { currentData: currentStatus, isLoading, isError } = useGetDocSetStatusQuery(!!docSetId ? docSetId : skipToken);

    if (!currentStatus || isLoading || isError) {
        return { isLoading: isLoading, info: undefined };
    }

    function getDetailsForStatus(status: DocSetStatus) {
        switch (status) {
            case DocSetStatus.Incomplete:
                return `This doc set ${docSetDocThreshold === undefined ? 'does not have enough documents' : `needs ${docSetDocThreshold} or more documents`} to be complete. Please add more documents to this doc set.`;
            default:
                return '';
        }
    }

    return {
        isLoading: isLoading,
        info: {
            status: currentStatus,
            label: getDocSetStatusLabel(currentStatus),
            details: getDetailsForStatus(currentStatus),
            statusColor: getDocSetStatusColor(currentStatus)
        }
    }
}